/**
 * applicationFormState — une question écrite de la candidature à la fois, comme l'ancien
 * `FormStepper` : la machine pure (le réducteur et ce qu'on lit dessus), sans React.
 *
 * Ce qu'elle tient : la question montrée (son rang parmi les questions visibles), les réponses, les
 * deux interrupteurs du profil (« J'ai un équivalent du baccalauréat passé à l'étranger », « J'ai déjà
 * le statut auto-entrepreneur », jamais gardés dans le brouillon, comme l'ancien), les précisions
 * libres, les erreurs de la question montrée, la confirmation de sortie, et la fin d'étape.
 */
import {
    type ApplicationQuestion,
    type ApplicationToggleKey,
    type ApplicationToggles,
    type ApplicationValues,
    questionErrors,
    visibleQuestions,
} from "../../../../adapters/tutor/Application/tutorApplicationQuestions"

export type ApplicationFormState = {
    index: number
    values: ApplicationValues
    toggles: ApplicationToggles
    precisions: Record<string, string>
    errors: Record<string, string>
    exitOpen: boolean
    /** L'étape est envoyée : la fin d'étape s'affiche. */
    done: boolean
}

export type ApplicationFormAction =
    | { type: "SET_VALUE"; name: string; value: unknown }
    | { type: "SET_TOGGLE"; key: ApplicationToggleKey; value: boolean }
    | { type: "SET_PRECISION"; name: string; value: string }
    | { type: "SET_ERRORS"; errors: Record<string, string> }
    | { type: "NEXT" }
    | { type: "BACK" }
    | { type: "OPEN_EXIT" }
    | { type: "CLOSE_EXIT" }
    | { type: "DONE" }

export function initialApplicationForm(restored: ApplicationValues | null | undefined): ApplicationFormState {
    return {
        index: 0,
        values: { ...(restored ?? {}) },
        toggles: { abroadBac: false, autoEntrepreneur: false },
        precisions: {},
        errors: {},
        exitOpen: false,
        done: false,
    }
}

export function applicationFormReducer(
    state: ApplicationFormState,
    action: ApplicationFormAction
): ApplicationFormState {
    switch (action.type) {
        case "SET_VALUE": {
            // Une langue qui reçoit son niveau efface l'erreur des langues (`handleLanguagesChange`) ;
            // un champ écrit garde la sienne jusqu'au prochain « Suivant », comme `react-hook-form`.
            const errors = { ...state.errors }
            delete errors[action.name]
            return {
                ...state,
                values: { ...state.values, [action.name]: action.value },
                errors: action.name === "foreign_languages" ? errors : state.errors,
            }
        }
        case "SET_TOGGLE":
            return { ...state, toggles: { ...state.toggles, [action.key]: action.value } }
        case "SET_PRECISION":
            return { ...state, precisions: { ...state.precisions, [action.name]: action.value } }
        case "SET_ERRORS":
            return { ...state, errors: action.errors }
        case "NEXT":
            return { ...state, index: state.index + 1, errors: {} }
        case "BACK":
            return state.index > 0 ? { ...state, index: state.index - 1, errors: {} } : state
        case "OPEN_EXIT":
            return { ...state, exitOpen: true }
        case "CLOSE_EXIT":
            return { ...state, exitOpen: false }
        case "DONE":
            return { ...state, done: true, exitOpen: false }
    }
}

/** La question montrée, parmi celles qui restent visibles (le masquage suit les réponses). */
export function currentQuestion(questions: ApplicationQuestion[], state: ApplicationFormState) {
    const visible = visibleQuestions(questions, state.values)
    const index = Math.min(state.index, Math.max(visible.length - 1, 0))
    return { visible, index, question: visible[index], isLast: index === visible.length - 1 }
}

/**
 * « Suivant » / « Terminer » : la question est-elle valide ? Rend ses erreurs (vides : on passe).
 * Les questions déjà passées l'ont été une fois valides ; seule celle du moment se relit.
 */
export function validateCurrent(question: ApplicationQuestion | undefined, state: ApplicationFormState) {
    return question == null ? {} : questionErrors(question, state.values, state.toggles)
}

/**
 * Quitter : l'ancien profil ne demandait confirmation qu'une fois une réponse écrite (`isDirty`),
 * un entretien toujours (`exitWarning`).
 */
export function exitNeedsConfirmation(always: boolean, state: ApplicationFormState, restored: ApplicationValues) {
    if (always) return true
    return Object.keys(state.values).some((key) => state.values[key] !== restored[key])
}
